import React, { useState } from 'react'
import {
  Box,
  Grid,
  Typography,
  Divider,
  Button,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import AnalyticsIcon from '@mui/icons-material/Analytics'
import EmailIcon from '@mui/icons-material/Email'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import WebhookIcon from '@mui/icons-material/Webhook'

import ConfigSection from '../components/ConfigSection'
import ConfigField from '../components/ConfigField'
import ConfigToggle from '../components/ConfigToggle'
import ConfigSelect from '../components/ConfigSelect'
import { IntegrationConfig } from '../types/EnvironmentConfig.types'
import { getServerBaseUrl } from '@/utils/serverUrl'
import { environmentConfigSectionDividerSx, environmentConfigSectionRootSx } from '../utils/environmentConfigChrome'

interface IntegrationSettingsProps {
  config: IntegrationConfig
  onChange: (updates: Partial<IntegrationConfig>) => void
  disabled?: boolean
  platformName?: string
}

/**
 * Integration Settings Section
 * Manages third-party integrations and external services
 */
const IntegrationSettings: React.FC<IntegrationSettingsProps> = ({
  config,
  onChange,
  disabled = false,
  platformName,
}) => {
  const theme = useTheme()
  const handleChange = (field: keyof IntegrationConfig, value: any) => {
    onChange({ [field]: value })
  }

  const handleNestedChange = (parentField: keyof IntegrationConfig, childField: string, value: any) => {
    const currentValue = config[parentField] as any
    onChange({
      [parentField]: {
        ...currentValue,
        [childField]: value,
      },
    })
  }

  return (
    <Box sx={environmentConfigSectionRootSx(theme)}>
      {/* Analytics Integrations */}
      <ConfigSection
        title="Analytics Integrations"
        description="Configure analytics and tracking services"
        icon={<AnalyticsIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ConfigToggle
              label="Google Analytics"
              value={config.analytics.googleAnalytics?.enabled || false}
              onChange={(value) => handleNestedChange('analytics', 'googleAnalytics', { 
                ...config.analytics.googleAnalytics, 
                enabled: value 
              })}
              description="Enable Google Analytics tracking"
              disabled={disabled}
            />
          </Grid>
          
          {config.analytics.googleAnalytics?.enabled && (
            <Grid item xs={12} md={6}>
              <ConfigField
                label="Google Analytics Tracking ID"
                value={config.analytics.googleAnalytics?.trackingId || ''}
                onChange={(value) => handleNestedChange('analytics', 'googleAnalytics', { 
                  ...config.analytics.googleAnalytics, 
                  trackingId: value 
                })}
                type="text"
                placeholder="G-XXXXXXXXXX"
                disabled={disabled}
                helpText="Google Analytics 4 tracking ID"
              />
            </Grid>
          )}
          
          <Grid item xs={12}>
            <ConfigToggle
              label="Mixpanel"
              value={config.analytics.mixpanel?.enabled || false}
              onChange={(value) => handleNestedChange('analytics', 'mixpanel', { 
                ...config.analytics.mixpanel, 
                enabled: value 
              })}
              description="Enable Mixpanel analytics tracking"
              disabled={disabled}
            />
          </Grid>
          
          {config.analytics.mixpanel?.enabled && (
            <Grid item xs={12} md={6}>
              <ConfigField
                label="Mixpanel Token"
                value={config.analytics.mixpanel?.token || ''}
                onChange={(value) => handleNestedChange('analytics', 'mixpanel', { 
                  ...config.analytics.mixpanel, 
                  token: value 
                })}
                type="text"
                placeholder="your-mixpanel-token"
                disabled={disabled}
                helpText="Mixpanel project token"
              />
            </Grid>
          )}
          
          <Grid item xs={12}>
            <ConfigToggle
              label="Hotjar"
              value={config.analytics.hotjar?.enabled || false}
              onChange={(value) => handleNestedChange('analytics', 'hotjar', { 
                ...config.analytics.hotjar, 
                enabled: value 
              })}
              description="Enable Hotjar user behavior tracking"
              disabled={disabled}
            />
          </Grid>
          
          {config.analytics.hotjar?.enabled && (
            <Grid item xs={12} md={6}>
              <ConfigField
                label="Hotjar Site ID"
                value={config.analytics.hotjar?.siteId || ''}
                onChange={(value) => handleNestedChange('analytics', 'hotjar', { 
                  ...config.analytics.hotjar, 
                  siteId: value 
                })}
                type="text"
                placeholder="1234567"
                disabled={disabled}
                helpText="Hotjar site ID"
              />
            </Grid>
          )}
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      {/* Email Provider Settings */}
      <ConfigSection
        title="Email Provider Settings"
        description="Configure email service provider and SMTP settings"
        icon={<EmailIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ConfigSelect
              label="Email Provider"
              value={config.email.provider}
              onChange={(value) => handleNestedChange('email', 'provider', value)}
              options={[
                { value: 'smtp', label: 'SMTP' },
                { value: 'sendgrid', label: 'SendGrid' },
                { value: 'mailgun', label: 'Mailgun' },
                { value: 'aws-ses', label: 'AWS SES' },
              ]}
              disabled={disabled}
              helpText="Choose your email service provider"
            />
          </Grid>
          
          {config.email.provider === 'mailgun' && (
            <>
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Mailgun API Key"
                  value={config.email.mailgun?.apiKey || ''}
                  onChange={(value) => handleNestedChange('email', 'mailgun', { 
                    ...config.email.mailgun, 
                    apiKey: value 
                  })}
                  type="password"
                  placeholder="key-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  disabled={disabled}
                  helpText="Your Mailgun API key"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Mailgun Domain"
                  value={config.email.mailgun?.domain || ''}
                  onChange={(value) => handleNestedChange('email', 'mailgun', { 
                    ...config.email.mailgun, 
                    domain: value 
                  })}
                  type="text"
                  placeholder="mg.yourdomain.com"
                  disabled={disabled}
                  helpText="Your Mailgun domain"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Sender Name"
                  value={config.email.mailgun?.senderName || ''}
                  onChange={(value) => handleNestedChange('email', 'mailgun', { 
                    ...config.email.mailgun, 
                    senderName: value 
                  })}
                  type="text"
                  placeholder="Platform Name"
                  disabled={disabled}
                  helpText="Display name for outgoing emails"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Reply-To Address"
                  value={config.email.mailgun?.replyTo || ''}
                  onChange={(value) => handleNestedChange('email', 'mailgun', { 
                    ...config.email.mailgun, 
                    replyTo: value 
                  })}
                  type="email"
                  placeholder="noreply@wiggin-industries.net"
                  disabled={disabled}
                  helpText="Address for email replies"
                />
              </Grid>
              
              <Grid item xs={12}>
                <MailgunTestSection 
                  config={config.email.mailgun} 
                  disabled={disabled}
                  platformName={platformName}
                />
              </Grid>
            </>
          )}

          {config.email.provider === 'smtp' && (
            <>
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="SMTP Host"
                  value={config.email.smtp?.host || ''}
                  onChange={(value) => handleNestedChange('email', 'smtp', { 
                    ...config.email.smtp, 
                    host: value 
                  })}
                  type="text"
                  placeholder="smtp.gmail.com"
                  disabled={disabled}
                  helpText="SMTP server hostname"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="SMTP Port"
                  value={config.email.smtp?.port?.toString() || ''}
                  onChange={(value) => handleNestedChange('email', 'smtp', { 
                    ...config.email.smtp, 
                    port: parseInt(value) || 587 
                  })}
                  type="number"
                  placeholder="587"
                  disabled={disabled}
                  helpText="SMTP server port"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="SMTP Username"
                  value={config.email.smtp?.username || ''}
                  onChange={(value) => handleNestedChange('email', 'smtp', { 
                    ...config.email.smtp, 
                    username: value 
                  })}
                  type="text"
                  placeholder="your-email@gmail.com"
                  disabled={disabled}
                  helpText="SMTP username"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="SMTP Password"
                  value={config.email.smtp?.password || ''}
                  onChange={(value) => handleNestedChange('email', 'smtp', { 
                    ...config.email.smtp, 
                    password: value 
                  })}
                  type="password"
                  placeholder="your-app-password"
                  disabled={disabled}
                  helpText="SMTP password or app password"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigToggle
                  label="Use Secure Connection"
                  value={config.email.smtp?.secure || false}
                  onChange={(value) => handleNestedChange('email', 'smtp', { 
                    ...config.email.smtp, 
                    secure: value 
                  })}
                  description="Use SSL/TLS for SMTP connection"
                  disabled={disabled}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Sender Name"
                  value={config.email.smtp?.senderName || ''}
                  onChange={(value) => handleNestedChange('email', 'smtp', { 
                    ...config.email.smtp, 
                    senderName: value 
                  })}
                  type="text"
                  placeholder="Platform Name"
                  disabled={disabled}
                  helpText="Display name for outgoing emails"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Reply-To Address"
                  value={config.email.smtp?.replyTo || ''}
                  onChange={(value) => handleNestedChange('email', 'smtp', { 
                    ...config.email.smtp, 
                    replyTo: value 
                  })}
                  type="email"
                  placeholder="noreply@wiggin-industries.net"
                  disabled={disabled}
                  helpText="Address for email replies"
                />
              </Grid>
              
              <Grid item xs={12}>
                <SMTPTestSection 
                  config={config.email.smtp} 
                  disabled={disabled}
                />
              </Grid>
            </>
          )}
          
          {config.email.provider === 'sendgrid' && (
            <Grid item xs={12} md={6}>
              <ConfigField
                label="SendGrid API Key"
                value={config.email.sendgrid?.apiKey || ''}
                onChange={(value) => handleNestedChange('email', 'sendgrid', { 
                  ...config.email.sendgrid, 
                  apiKey: value 
                })}
                type="password"
                placeholder="SG.your-api-key"
                disabled={disabled}
                helpText="SendGrid API key"
              />
            </Grid>
          )}
          
          
          {config.email.provider === 'aws-ses' && (
            <>
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="AWS Access Key ID"
                  value={config.email.awsSes?.accessKeyId || ''}
                  onChange={(value) => handleNestedChange('email', 'awsSes', { 
                    ...config.email.awsSes, 
                    accessKeyId: value 
                  })}
                  type="text"
                  placeholder="AKIAIOSFODNN7EXAMPLE"
                  disabled={disabled}
                  helpText="AWS access key ID"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="AWS Secret Access Key"
                  value={config.email.awsSes?.secretAccessKey || ''}
                  onChange={(value) => handleNestedChange('email', 'awsSes', { 
                    ...config.email.awsSes, 
                    secretAccessKey: value 
                  })}
                  type="password"
                  placeholder="your-secret-key"
                  disabled={disabled}
                  helpText="AWS secret access key"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="AWS Region"
                  value={config.email.awsSes?.region || ''}
                  onChange={(value) => handleNestedChange('email', 'awsSes', { 
                    ...config.email.awsSes, 
                    region: value 
                  })}
                  type="text"
                  placeholder="us-east-1"
                  disabled={disabled}
                  helpText="AWS region for SES"
                />
              </Grid>
            </>
          )}
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      {/* OAuth Settings */}
      <ConfigSection
        title="OAuth Settings"
        description="Configure OAuth providers for social login"
        icon={<AccountCircleIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ConfigToggle
              label="Google OAuth"
              value={config.oauth.google?.enabled || false}
              onChange={(value) => handleNestedChange('oauth', 'google', { 
                ...config.oauth.google, 
                enabled: value 
              })}
              description="Enable Google OAuth login"
              disabled={disabled}
            />
          </Grid>
          
          {config.oauth.google?.enabled && (
            <>
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Google Client ID"
                  value={config.oauth.google?.clientId || ''}
                  onChange={(value) => handleNestedChange('oauth', 'google', { 
                    ...config.oauth.google, 
                    clientId: value 
                  })}
                  type="text"
                  placeholder="your-google-client-id"
                  disabled={disabled}
                  helpText="Google OAuth client ID"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Google Client Secret"
                  value={config.oauth.google?.clientSecret || ''}
                  onChange={(value) => handleNestedChange('oauth', 'google', { 
                    ...config.oauth.google, 
                    clientSecret: value 
                  })}
                  type="password"
                  placeholder="your-google-client-secret"
                  disabled={disabled}
                  helpText="Google OAuth client secret"
                />
              </Grid>
            </>
          )}
          
          <Grid item xs={12}>
            <ConfigToggle
              label="GitHub OAuth"
              value={config.oauth.github?.enabled || false}
              onChange={(value) => handleNestedChange('oauth', 'github', { 
                ...config.oauth.github, 
                enabled: value 
              })}
              description="Enable GitHub OAuth login"
              disabled={disabled}
            />
          </Grid>
          
          {config.oauth.github?.enabled && (
            <>
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="GitHub Client ID"
                  value={config.oauth.github?.clientId || ''}
                  onChange={(value) => handleNestedChange('oauth', 'github', { 
                    ...config.oauth.github, 
                    clientId: value 
                  })}
                  type="text"
                  placeholder="your-github-client-id"
                  disabled={disabled}
                  helpText="GitHub OAuth client ID"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="GitHub Client Secret"
                  value={config.oauth.github?.clientSecret || ''}
                  onChange={(value) => handleNestedChange('oauth', 'github', { 
                    ...config.oauth.github, 
                    clientSecret: value 
                  })}
                  type="password"
                  placeholder="your-github-client-secret"
                  disabled={disabled}
                  helpText="GitHub OAuth client secret"
                />
              </Grid>
            </>
          )}
          
          <Grid item xs={12}>
            <ConfigToggle
              label="Microsoft OAuth"
              value={config.oauth.microsoft?.enabled || false}
              onChange={(value) => handleNestedChange('oauth', 'microsoft', { 
                ...config.oauth.microsoft, 
                enabled: value 
              })}
              description="Enable Microsoft OAuth login"
              disabled={disabled}
            />
          </Grid>
          
          {config.oauth.microsoft?.enabled && (
            <>
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Microsoft Client ID"
                  value={config.oauth.microsoft?.clientId || ''}
                  onChange={(value) => handleNestedChange('oauth', 'microsoft', { 
                    ...config.oauth.microsoft, 
                    clientId: value 
                  })}
                  type="text"
                  placeholder="your-microsoft-client-id"
                  disabled={disabled}
                  helpText="Microsoft OAuth client ID"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Microsoft Client Secret"
                  value={config.oauth.microsoft?.clientSecret || ''}
                  onChange={(value) => handleNestedChange('oauth', 'microsoft', { 
                    ...config.oauth.microsoft, 
                    clientSecret: value 
                  })}
                  type="password"
                  placeholder="your-microsoft-client-secret"
                  disabled={disabled}
                  helpText="Microsoft OAuth client secret"
                />
              </Grid>
            </>
          )}
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      {/* Webhook Settings */}
      <ConfigSection
        title="Webhook Settings"
        description="Configure webhook endpoints for external integrations"
        icon={<WebhookIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ConfigToggle
              label="Enable Webhooks"
              value={config.webhooks.enabled}
              onChange={(value) => handleNestedChange('webhooks', 'enabled', value)}
              description="Enable webhook notifications to external services"
              disabled={disabled}
            />
          </Grid>
          
          {config.webhooks.enabled && (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Webhook endpoints can be configured through the API or webhook management interface.
              </Typography>
            </Grid>
          )}
        </Grid>
      </ConfigSection>
    </Box>
  )
}

/**
 * SMTP Test Section Component
 * Allows testing SMTP configuration by sending a test email
 */
interface SMTPTestSectionProps {
  config?: {
    host?: string
    port?: number
    username?: string
    password?: string
    secure?: boolean
  }
  disabled?: boolean
}

const SMTPTestSection: React.FC<SMTPTestSectionProps> = ({ config, disabled }) => {
  const theme = useTheme()
  const [testEmail, setTestEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  const handleTestEmail = async () => {
    if (!testEmail.trim()) {
      setMessage({ type: 'error', text: 'Please enter a test email address' })
      return
    }

    setIsLoading(true)
    setMessage(null)

    try {
      // Get current user session token for authentication
      const { authService } = await import('../../../../../../services/back4app')
      const currentUser = authService.getCurrentUser?.()
      const sessionToken = currentUser?.getSessionToken?.()

      if (!sessionToken) {
        setMessage({ 
          type: 'error', 
          text: 'You must be logged in to test emails. Authentication required.' 
        })
        setIsLoading(false)
        return
      }

      const baseUrl = getServerBaseUrl()
      const isDev = !!import.meta.env.DEV
      const fullUrl = isDev ? '/api/email/test' : `${baseUrl}/api/email/test`
      
      // Use centralized email test endpoint
      const testResponse = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        credentials: isDev ? 'same-origin' : 'include',
        body: JSON.stringify({
          recipientEmail: testEmail.trim(),
          emailType: 'account-created' // Use account-created as a simple test email type
        })
      })

      const testData = await testResponse.json()

      if (testResponse.ok && testData.success) {
        setMessage({ 
          type: 'success', 
          text: testData.message || `Test email sent successfully to ${testEmail}` 
        })
      } else {
        throw new Error(testData.error || testData.message || 'Failed to send test email')
      }
    } catch (error: any) {
      console.error('Email test error:', error)
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to send test email. Make sure MAILGUNUSER and MAILGUNPASSWORD are set in the server environment.' 
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', backgroundImage: 'none' }}>
      <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
        Test SMTP Configuration
      </Typography>
      
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Test Email Address"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="test@example.com"
            disabled={disabled || isLoading}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'text.primary',
                '& fieldset': {
                  borderColor: 'divider',
                },
                '&:hover fieldset': {
                  borderColor: alpha(theme.palette.text.primary, 0.35),
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'info.main',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: 'info.main',
                },
              },
            }}
          />
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Button
            variant="contained"
            color="info"
            onClick={handleTestEmail}
            disabled={disabled || isLoading || !testEmail.trim()}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              '&:disabled': {
                backgroundColor: theme.palette.action.disabledBackground,
                color: 'text.disabled',
              },
            }}
          >
            {isLoading ? 'Sending...' : 'Send Test Email'}
          </Button>
        </Grid>
      </Grid>

      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mt: 2 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
        This will send a test email using the centralized Mailgun email service to verify your email configuration is working correctly.
        Make sure MAILGUNUSER and MAILGUNPASSWORD are set in the server environment.
      </Typography>
      
      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
        <strong>Note:</strong> The system uses Mailgun SMTP for all email sending. Set MAILGUNUSER and MAILGUNPASSWORD in the server environment to enable email functionality.
      </Typography>
    </Box>
  )
}

// Mailgun Test Section Component
const MailgunTestSection: React.FC<{
  config: any
  disabled: boolean
  platformName?: string
}> = ({ config, disabled, platformName }) => {
  const theme = useTheme()
  const [testEmail, setTestEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null)

  const handleTestEmail = async () => {
    if (!testEmail.trim()) {
      setMessage({ type: 'error', text: 'Please enter a test email address' })
      return
    }

    if (!config?.apiKey || !config?.domain) {
      setMessage({ type: 'error', text: 'Please configure Mailgun API key and domain first' })
      return
    }

    setIsLoading(true)
    setMessage(null)

    try {
      const baseUrl = getServerBaseUrl()
      const testResponse = await fetch(`${baseUrl}/api/mailgun/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: testEmail.trim(),
          subject: `Mailgun Configuration Test - ${platformName || 'Platform'}`,
          html: `
            <h2>🎉 Mailgun Test Email</h2>
            <p>Congratulations! Your Mailgun configuration is working properly.</p>
            <p><strong>Configuration Details:</strong></p>
            <ul>
              <li>Provider: Mailgun</li>
              <li>Domain: ${config.domain}</li>
              <li>Sender: ${config.senderName || platformName || 'Platform'}</li>
              <li>Sent at: ${new Date().toISOString()}</li>
            </ul>
            <p>If you received this email, your Mailgun configuration is working correctly!</p>
            <hr>
            <p><em>This is a test email from ${platformName || 'Platform'}</em></p>
          `,
          text: `
            Mailgun Test Email
            
            Congratulations! Your Mailgun configuration is working properly.
            
            Configuration Details:
            - Provider: Mailgun
            - Domain: ${config.domain}
            - Sender: ${config.senderName || platformName || 'Platform'}
            - Sent at: ${new Date().toISOString()}
            
            If you received this email, your Mailgun configuration is working correctly!
            
            This is a test email from ${platformName || 'Platform'}
          `,
          fromName: config.senderName || platformName || 'Platform',
          fromEmail: `noreply@${config.domain}`,
          mailgunConfig: {
            apiKey: config.apiKey,
            domain: config.domain,
            senderName: config.senderName || platformName || 'Platform',
            replyTo: config.replyTo || 'noreply@wiggin-industries.net'
          }
        })
      })

      const testData = await testResponse.json()

      if (testResponse.ok && testData.success) {
        const messageId = testData.result?.messageId || testData.messageId
        setMessage({
          type: 'success',
          text: `Test email sent successfully to ${testEmail}. Message ID: ${messageId}`
        })
      } else {
        throw new Error(testData.error || 'Failed to send test email')
      }
    } catch (error: any) {
      console.error('Mailgun test error:', error)
      setMessage({
        type: 'error',
        text: `SMTP test error: ${error.message}`
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', backgroundImage: 'none' }}>
      <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
        Test Mailgun Configuration
      </Typography>
      
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Test Email Address"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="test@example.com"
            disabled={disabled || isLoading}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'text.primary',
                '& fieldset': { borderColor: 'divider' },
                '&:hover fieldset': { borderColor: alpha(theme.palette.text.primary, 0.35) },
                '&.Mui-focused fieldset': { borderColor: 'info.main' }
              },
              '& .MuiInputLabel-root': { color: 'text.secondary' },
              '& .MuiInputLabel-root.Mui-focused': { color: 'info.main' }
            }}
          />
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Button
            fullWidth
            variant="contained"
            color="info"
            onClick={handleTestEmail}
            disabled={disabled || isLoading || !testEmail.trim()}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
            sx={{
              '&:disabled': {
                backgroundColor: theme.palette.action.disabledBackground,
                color: 'text.disabled',
              },
            }}
          >
            {isLoading ? 'Sending...' : 'Send Test Email'}
          </Button>
        </Grid>
      </Grid>

      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mt: 2 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', fontSize: '0.75rem' }}>
        <strong>Mailgun Configuration Tips:</strong><br/>
        • Get your API key from Mailgun dashboard<br/>
        • Use your verified domain (e.g., mg.yourdomain.com)<br/>
        • Test with a real email address to verify delivery<br/>
        • Check Mailgun logs for delivery status
      </Typography>
    </Box>
  )
}

export default IntegrationSettings 